import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Truck, Eye, EyeOff, User, Mail, Lock, Phone, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const REMEMBER_KEY = 'truckpro_remember';

export const LoginPage = () => {
  const { login, register, error, clearError } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    license_number: ''
  });

  // Charger les identifiants sauvegardés au démarrage
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      try {
        const { email, password } = JSON.parse(saved);
        setFormData(prev => ({ ...prev, email, password }));
        setRememberMe(true);
      } catch (e) {
        localStorage.removeItem(REMEMBER_KEY);
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) clearError();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          // Sauvegarder ou supprimer les identifiants selon le choix
          if (rememberMe) {
            localStorage.setItem(REMEMBER_KEY, JSON.stringify({
              email: formData.email,
              password: formData.password
            }));
          } else {
            localStorage.removeItem(REMEMBER_KEY);
          }
          toast.success('Connexion réussie!');
        } else {
          toast.error(result.error);
        }
      } else {
        const result = await register(formData);
        if (result.success) {
          toast.success('Compte créé avec succès!');
        } else {
          toast.error(result.error);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    clearError();
  };

  return (
    <div className="login-bg min-h-screen flex flex-col" data-testid="login-page">
      {/* Background Image */}
      <div 
        className="absolute inset-0 opacity-20 bg-cover bg-center"
        style={{ 
          backgroundImage: `url('https://images.unsplash.com/photo-1731531702939-0b0a9733501e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHx0cnVjayUyMGRyaXZlciUyMGRyaXZpbmclMjBuaWdodHxlbnwwfHx8fDE3NzI4ODk0NzB8MA&ixlib=rb-4.1.0&q=85')`
        }}
      />
      
      {/* Content */}
      <div className="relative z-10 flex-1 flex flex-col p-6">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-8 mt-8">
          <div className="w-16 h-16 rounded-xl overflow-hidden flex items-center justify-center bg-slate-900 ring-1 ring-amber-500/30">
            <img src="/icons/icon-192.png" alt="ProPilote" className="w-full h-full object-cover" />
          </div>
          <div>
            <h1 className="login-logo" data-testid="app-logo">ProPilote</h1>
            <p className="text-slate-400 text-sm font-medium -mt-1">App Chauffeur</p>
          </div>
        </div>

        {/* Form Card */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-md bg-slate-900/80 backdrop-blur-xl border border-slate-800 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              {isLogin ? 'Connexion' : 'Créer un compte'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="label-uppercase">Prénom</Label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                        <Input
                          name="first_name"
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="Jean"
                          className="input-large pl-12"
                          required={!isLogin}
                          data-testid="input-first-name"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="label-uppercase">Nom</Label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                        <Input
                          name="last_name"
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Tremblay"
                          className="input-large pl-12"
                          required={!isLogin}
                          data-testid="input-last-name"
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <Label className="label-uppercase">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                      <Input
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+1 514 555 1234"
                        className="input-large pl-12"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="label-uppercase">N° Permis <span className="text-slate-500 lowercase font-normal">(facultatif)</span></Label>
                    <div className="relative">
                      <CreditCard className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                      <Input
                        name="license_number"
                        value={formData.license_number}
                        onChange={handleChange}
                        placeholder="Laisser vide si non desire"
                        className="input-large pl-12"
                        data-testid="input-license"
                      />
                    </div>
                    <p className="text-xs text-slate-400 mt-1">Champ facultatif. Aucun lien ELD. Donnees chiffrees et non partagees.</p>
                  </div>
                </>
              )}

              <div>
                <Label className="label-uppercase">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="chauffeur@exemple.com"
                    className="input-large pl-12"
                    required
                    data-testid="input-email"
                  />
                </div>
              </div>

              <div>
                <Label className="label-uppercase">Mot de passe</Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={20} />
                  <Input
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="••••••••"
                    className="input-large pl-12 pr-12"
                    required
                    data-testid="input-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Se souvenir de moi - uniquement en mode connexion */}
              {isLogin && (
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setRememberMe(!rememberMe)}
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                      rememberMe 
                        ? 'bg-amber-500 border-amber-500' 
                        : 'border-slate-600 hover:border-slate-500'
                    }`}
                    data-testid="remember-me-checkbox"
                  >
                    {rememberMe && (
                      <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                  <label 
                    className="text-slate-400 text-sm cursor-pointer select-none"
                    onClick={() => setRememberMe(!rememberMe)}
                  >
                    Se souvenir de moi
                  </label>
                </div>
              )}

              {error && (
                <div className="bg-red-900/30 border border-red-800 rounded-lg p-3 text-red-400 text-sm" data-testid="error-message">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                className="w-full btn-large btn-primary-glow mt-6"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Chargement...
                  </span>
                ) : (
                  isLogin ? 'Se connecter' : 'Créer le compte'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={toggleMode}
                className="text-amber-500 hover:text-amber-400 font-medium transition-colors"
                data-testid="toggle-mode"
              >
                {isLogin ? "Pas de compte ? S'inscrire" : 'Déjà un compte ? Se connecter'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
